import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';

import { getApiKeyMutationKey } from '.';
import { apiKeyService } from '../../application/ApiKeyService';
import { OrganizationActionType } from '../../context/organization/actions';
import { useOrganizationContext } from '../../context/organization/context';
import { ApiKey } from '../../domain/apiKey/ApiKey';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveUser } from '../user/useRetrieveUser';

type UseRetrieveApiKeysResponse = ServiceResponse<Function, ApiKey[]>;

// Hooks act as 'Adapter' layer.
export const useRetrieveApiKeys = (): UseRetrieveApiKeysResponse => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: user } = useRetrieveUser();
  const { organizationState, organizationDispatch } = useOrganizationContext();
  const { sessionExpiredLogout } = useErrorMiddleware();

  const {
    isInitialLoading: isLoading,
    refetch,
    isError,
    isSuccess,
  } = useQuery([getApiKeyMutationKey(), user!.id], () => apiKeyService.fetchAll(), {
    enabled: !!user,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    onSuccess: (apiKeys) => {
      organizationDispatch({
        type: OrganizationActionType.FETCH_APIKEYS_SUCCESS,
        payload: {
          apiKeys,
        },
      });
    },
    onError: (err: any) => {
      console.info(`ApiKeys Retrieve Error: `, err?.response);

      if (err?.response?.status === 401) {
        sessionExpiredLogout();
        return;
      }

      showSnackbarMessage({
        messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
      });
    },
  });

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    run: refetch,
    data: organizationState.apiKeys,
  };
};
